import { getArrangementStatus } from "helpers/at-need";
import { useGET } from "hooks/useAPI";
import {
  areStartHubItemsDone,
  getArrangementHubStatus,
  getConsultationHubStatus,
  getVitalsHubStatus,
  hasBookedConsultationFromState,
  hasScheduledConsultation,
  shouldShowCompletedStartHubFromState,
  shouldSkipStartHubFromState,
  shouldShowStartHub,
} from "./startHubStatus";
import { useHubTaskStates } from "./useHubTaskStates";

type VitalsSubmissionsResponse = {
  results?: unknown[];
};

type UseStartHubStateArgs = {
  appDataError: any;
  appDataRaw: unknown;
  caseId: string | undefined;
  caseStage: string | null | undefined;
  hasCase: boolean;
  isArrangementFlowEnabled: boolean | undefined;
  isCareSpecialist: boolean;
  locationState: unknown;
};

export function useStartHubState({
  appDataError,
  appDataRaw,
  caseId,
  caseStage,
  hasCase,
  isArrangementFlowEnabled,
  isCareSpecialist,
  locationState,
}: UseStartHubStateArgs) {
  const {
    data: vitalsSubmissionsData,
    isLoading: isLoadingVitals,
    error: vitalsSubmissionsError,
  } = useGET(
    caseId && isArrangementFlowEnabled === true
      ? `/cases/${caseId}/vitals/submissions`
      : null,
    undefined,
    {
      shouldRetryOnError: (error: any) => error?.statusCode !== 404,
    },
  );

  const {
    vitalsTaskState,
    arrangementTaskState,
    consultationTaskState,
    isLoadingTasks,
  } = useHubTaskStates(caseId, {
    enabled: isArrangementFlowEnabled === true,
  });

  const appDataNotFound =
    (appDataError as { statusCode?: number } | undefined)?.statusCode === 404;
  const appDataBlockingError = !!appDataError && !appDataNotFound;
  const vitalsBlockingError =
    !!vitalsSubmissionsError &&
    (vitalsSubmissionsError as { statusCode?: number }).statusCode !== 404;
  const arrangementStatus = getArrangementStatus(appDataRaw);
  const hasVitalsSubmission =
    (
      (vitalsSubmissionsData as VitalsSubmissionsResponse | undefined)
        ?.results ?? []
    ).length > 0;
  const vitalsHubStatus = getVitalsHubStatus({
    hasSubmission: hasVitalsSubmission,
    taskState: vitalsTaskState,
  });
  const arrangementHubStatus = getArrangementHubStatus(
    arrangementStatus,
    arrangementTaskState,
  );
  const hasConsultationBooking =
    hasScheduledConsultation(appDataRaw) ||
    hasBookedConsultationFromState(locationState);
  const consultationHubStatus = getConsultationHubStatus({
    hasConsultationBooking,
    vitalsStatus: vitalsHubStatus,
    arrangementStatus: arrangementHubStatus,
    stage: caseStage,
    taskState: consultationTaskState,
  });
  const allStartHubItemsDone = areStartHubItemsDone({
    vitalsStatus: vitalsHubStatus,
    arrangementStatus: arrangementHubStatus,
    consultationStatus: consultationHubStatus,
  });
  const shouldShowCompletedStartHub =
    !isCareSpecialist &&
    isArrangementFlowEnabled === true &&
    shouldShowCompletedStartHubFromState(locationState) &&
    allStartHubItemsDone;
  const canEvaluateStartHub =
    hasCase && !appDataBlockingError && !vitalsBlockingError;
  const shouldSkipStartHub =
    !isCareSpecialist && shouldSkipStartHubFromState(locationState);
  const showStartHub =
    canEvaluateStartHub &&
    !shouldSkipStartHub &&
    (shouldShowCompletedStartHub ||
      shouldShowStartHub({
        isCareSpecialist,
        arrangementFlowEnabled: isArrangementFlowEnabled === true,
        vitalsStatus: vitalsHubStatus,
        arrangementStatus: arrangementHubStatus,
        consultationStatus: consultationHubStatus,
      }));
  const hubEvaluationError =
    !isCareSpecialist &&
    isArrangementFlowEnabled === true &&
    (appDataBlockingError || vitalsBlockingError);
  // The vitals fetch only feeds the legacy fallback: once the Task System
  // answers for vitals, its loading state must not hold the hub decision.
  const isHubDecisionPending =
    !isCareSpecialist &&
    isArrangementFlowEnabled === true &&
    (isLoadingTasks || (isLoadingVitals && vitalsTaskState === "unavailable"));

  return {
    allStartHubItemsDone,
    arrangementHubStatus,
    consultationHubStatus,
    hubEvaluationError,
    isHubDecisionPending,
    showStartHub,
    vitalsHubStatus,
  };
}
